import columnDefinition from "./columnDefinition";
import { useSelector } from "react-redux";
import { pbxIdSelector } from "@/react/features/customer/customerSlice";
import {
  handleUpdateResponse,
  useGetIcfAndExtensionsQuery,
  useUpdateIcfAndExtensionsMutation,
} from "./api";
import { errorNotification, useErrorNotification } from "@/react/notifications";
import schema from "./schema";
import React, { useEffect, useMemo, useState } from "react";
import TableComponent from "@/react/components/table/TableComponent";
import { useTable } from "@/react/components/table/useTable";
import PageLayout from "@/react/features/styled/PageLayout";
import { CustomerCard } from "@/react/features/customer";
import PageTitle from "@/react/components/PageTitle";
import ToggleButton from "@8x8/oxygen-toggle-button";
import LabeledSelect from "@/react/components/LabeledSelect";
import styled from "styled-components";
import { COLORS } from "@/react/theme/constants";
import PageContentContainer from "@/react/features/styled/PageContentContainer";
import {
  DIAL_PLAN_RULE_SETS,
  INITIAL_ICF_ERRORS,
  INITIAL_ICF_STATE,
} from "./constants";
import lodash from "lodash";
import { filterTableMutations } from "@/react/components/table/filterTableMutations";
import { ErrorMessage } from "./ErrorMessage";
import useIcfMutation from "@/react/features/support-tools/international-call-forwarding/useIcfMutation";

const MAX_CALL_FORWARDING_OPTIONS = Object.freeze(
  ["0", "1", "2"].map((option) => ({ label: option, value: option }))
);

const SettingsSection = styled.div`
  width: fit-content;
  padding: 16px;
  border: 1px solid ${COLORS.tertiary};
  border-radius: 8px;
  margin-bottom: 24px;
  margin-top: 16px;
`;

const columns = columnDefinition();

const InternationalCallForwardingPage = () => {
  const pbxId = useSelector(pbxIdSelector);

  const [icfSettings, setIcfSettings] = useState(INITIAL_ICF_STATE);
  const [icfSettingsErrors, setIcfSettingsErrors] =
    useState(INITIAL_ICF_ERRORS);

  const {
    data: icf,
    isFetching,
    error,
    refetch,
  } = useGetIcfAndExtensionsQuery({ pbxId });
  const [updateIcf, { isLoading: isUpdating }] =
    useUpdateIcfAndExtensionsMutation();

  useEffect(() => {
    setIcfSettings({
      allowServiceExtensions: icf?.allowServiceExtensions,
      maxCallForwarding: icf?.maxCallForwarding,
      allowTransfers: icf?.allowTransfers,
    });
  }, [icf]);

  const areSettingsChanged = useMemo(
    () =>
      !lodash.isEqual(icfSettings, {
        allowServiceExtensions: icf?.allowServiceExtensions,
        maxCallForwarding: icf?.maxCallForwarding,
        allowTransfers: icf?.allowTransfers,
      }),
    [icfSettings, icf]
  );

  useErrorNotification(error);

  const extensions = useMemo(
    () =>
      (icf?.serviceTypeExtensions || []).filter((extension) =>
        DIAL_PLAN_RULE_SETS.includes(extension.dialPlanRuleSetName)
      ),
    [icf]
  );

  const idKey = schema.fields.subscriptionId.spec.key;

  const { table, filters, state, mutations } = useTable(
    extensions,
    columns,
    schema,
    idKey
  );

  const { rowsToUpdate, resetData, setData, setRowsToUpdate } = mutations;

  const isDirty = useMemo(
    () => rowsToUpdate.size > 0 || areSettingsChanged,
    [areSettingsChanged, rowsToUpdate]
  );

  const onSave = useIcfMutation({
    rowsToUpdate,
    areSettingsChanged,
    icfSettings,
    updateIcf,
    pbxId,
    handleUpdateResponse,
    setRowsToUpdate,
    setData,
    idKey,
    setIcfSettingsErrors,
    icfSettingsErrors,
    errorNotification,
  });

  const finalMutations = useMemo(
    () => filterTableMutations(mutations, { update: true }),
    [mutations]
  );

  const isLoading = isFetching || isUpdating;

  return (
    <PageLayout>
      <CustomerCard hasPbxSelector={true} />
      <PageContentContainer>
        <PageTitle>International Call Forwarding</PageTitle>
        <SettingsSection>
          <ToggleButton
            label={"Allow International Forwarding for Service Extensions"}
            isChecked={icfSettings.allowServiceExtensions}
            onChange={() =>
              setIcfSettings({
                ...icfSettings,
                allowServiceExtensions: !icfSettings.allowServiceExtensions,
              })
            }
            isDisabled={isLoading}
          />
          <ErrorMessage message={icfSettingsErrors.allowServiceExtensions} />
          <ToggleButton
            label={"Allow International Transfers"}
            isChecked={icfSettings.allowTransfers}
            onChange={() =>
              setIcfSettings({
                ...icfSettings,
                allowTransfers: !icfSettings.allowTransfers,
              })
            }
            isDisabled={isLoading}
          />
          <ErrorMessage message={icfSettingsErrors.allowTransfers} />
          <LabeledSelect
            orientation={"inline"}
            selectWidth={100}
            prompt={"Maximum international forwards"}
            placeholder={"Select..."}
            isDisabled={isLoading}
            options={MAX_CALL_FORWARDING_OPTIONS}
            value={icfSettings?.maxCallForwarding}
            onSelectionChanged={(opt) =>
              setIcfSettings({ ...icfSettings, maxCallForwarding: opt })
            }
          />
          <ErrorMessage message={icfSettingsErrors.maxCallForwarding} />
        </SettingsSection>
        <TableComponent
          title="Call Waiting"
          schema={schema}
          isDirty={isDirty}
          isLoading={isLoading}
          onSave={onSave}
          onCancel={resetData}
          onRefresh={refetch}
          table={table}
          filters={filters}
          mutations={finalMutations}
          state={state}
        />
      </PageContentContainer>
    </PageLayout>
  );
};

export default InternationalCallForwardingPage;
